import { useEffect, useRef } from 'react';
import type { Place } from '../types';
import { ASSURANCE, TYPE_LABEL, formatSince } from '../lib/trust';
import { CertificateStrip } from './CertificateStrip';
import { TypeGlyph } from '../lib/glyphs';
import { formatDistance } from './PlaceRow';
import './PlaceDetail.css';
import { useT } from '../i18n';

interface Props {
    place: Place;
    saved: boolean;
    onToggleSave: (place: Place) => void;
    onBack: () => void;
}

/** Pretty-print E.164 back into something dialable-looking. */
function prettyPhone(e164: string): string {
    const m = e164.match(/^\+(\d{1,3})(\d+)$/);
    if (!m) return e164;
    const rest = m[2]!;
    return `+${m[1]} ${rest.replace(/(\d{2,4})(?=\d)/g, '$1 ').trim()}`;
}

export function PlaceDetail({ place, saved, onToggleSave, onBack }: Props) {
    const { t, lang } = useT();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const assurance = place.halal ? ASSURANCE[place.halal.status] : null;
    const primary = place.sources.find((s) => !s.role) ?? place.sources[0];
    const approximate = place.precision === 'district' || place.precision === 'city';

    // Move focus to the heading so screen-reader and keyboard users land in the
    // detail rather than being left where the list row used to be.
    useEffect(() => { headingRef.current?.focus(); }, [place.id]);

    return (
        <article className="detail">
            <div className="detail__bar">
                <button type="button" className="detail__back" onClick={onBack}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                    {t('place.back')}
                </button>
                <button
                    type="button"
                    className={`detail__save ${saved ? 'is-saved' : ''}`}
                    onClick={() => onToggleSave(place)}
                    aria-pressed={saved}
                >
                    <svg viewBox="0 0 24 24" width="17" height="17" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                    </svg>
                    {saved ? t('place.saved') : t('place.save')}
                </button>
            </div>

            <header className="detail__head">
                <span className="detail__glyph" aria-hidden="true"><TypeGlyph type={place.type} size={22} /></span>
                <div>
                    <h2 className="detail__name" tabIndex={-1} ref={headingRef}>{place.name}</h2>
                    {place.nameLocal && place.nameLocal !== place.name && (
                        <p className="detail__name-local" lang="zh">{place.nameLocal}</p>
                    )}
                    <p className="detail__type">
                        {t(TYPE_LABEL[place.type])}
                        {place.cuisines?.length ? ` · ${place.cuisines.join(', ')}` : ''}
                        {place.distance != null ? ` · ${t('place.away', { distance: formatDistance(place.distance) })}` : ''}
                    </p>
                </div>
            </header>

            <CertificateStrip place={place} />

            {assurance && !['certified', 'certified_kitchen', 'muslim_friendly'].includes(place.halal!.status) && (
                <p className="detail__claim">
                    <strong>{t(assurance.label)}.</strong> {t(assurance.detail)}
                </p>
            )}

            <dl className="detail__facts">
                {place.address && (
                    <div>
                        <dt>{t('place.address')}</dt>
                        <dd>
                            {place.address}
                            {place.addressLocal && <><br /><span lang="zh">{place.addressLocal}</span></>}
                            {approximate && (
                                <span className="detail__approx">
                                    {t('place.approximate', { district: place.district || t('place.theDistrict') })}
                                </span>
                            )}
                        </dd>
                    </div>
                )}
                {place.openingHours && (<div><dt>{t('place.hours')}</dt><dd>{place.openingHours}</dd></div>)}
                {place.phone && (
                    <div><dt>{t('place.phone')}</dt><dd><a href={`tel:${place.phone}`}>{prettyPhone(place.phone)}</a></dd></div>
                )}
            </dl>

            <div className="detail__actions">
                <a
                    className="btn btn--primary"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('place.directions')}
                </a>
                {place.website && (
                    <a className="btn" href={place.website} target="_blank" rel="noopener noreferrer">{t('place.website')}</a>
                )}
                <button
                    type="button"
                    className="btn"
                    onClick={() => {
                        const url = `${location.origin}/city/${place.city}/place/${encodeURIComponent(place.id)}`;
                        if (navigator.share) void navigator.share({ title: place.name, url });
                        else void navigator.clipboard?.writeText(url);
                    }}
                >
                    {t('place.share')}
                </button>
            </div>

            <footer className="detail__source">
                <span>
                    {primary?.url
                        ? <a href={primary.url} target="_blank" rel="noopener noreferrer">{t('place.listedBy', { source: primary.name })}</a>
                        : t('place.listedBy', { source: primary?.name ?? '' })}
                </span>
                {place.liveCheckedAt && (
                    <span>{t('place.checkedWhen', { when: formatSince(place.liveCheckedAt, t, lang.code) })}</span>
                )}
                {primary?.license && <span className="detail__licence">{primary.license}</span>}
            </footer>
        </article>
    );
}
